import invoiceService from "@/services/invoice.service";
import { getStripeDocuments } from "@/services/stripe-documents.service";
import { getErrorMessage } from "@/utils/error-message";

const initialState = {
  invoices: undefined,
  // A user-ready sentence, not an error object — the component renders it as-is.
  error: "",
  loading: false,

  /* ── Stripe paperwork ───────────────────────────────────────────────────
     A Map keyed by Stripe id, so the table can look a document up per row
     instead of scanning a list. Vue 3's reactivity handles Map natively, so a
     `.get()` inside a computed tracks correctly.

     `undefined` means "not fetched yet", an empty Map means "fetched, this
     user has none". The table needs to tell those apart: the first is a
     pending state where a download button should not yet be called absent, the
     second is a settled answer.

     Kept SEPARATE from `invoices` rather than merged into each record on
     arrival. The two come from different endpoints with different failure
     modes, and merging would mean a Stripe outage either blocked the payments
     list or silently produced records missing a field that was there a moment
     ago. Separate state lets the list render fully while the documents are
     still in flight or have failed outright. */
  documents: undefined,
  documentsError: "",
  documentsLoading: false,
};

export const invoices = {
  namespaced: true,
  state: initialState,
  actions: {
    /*
     * Returns the promise (it previously returned undefined, so `dispatch`
     * resolved immediately and callers could not tell a failure from a slow
     * response) and records failures in state instead of rejecting — the old
     * version had no `.catch` at all, which made every failed load both
     * invisible to the user and an unhandled promise rejection.
     */
    getInvoices({ commit }, payload = null) {
      commit("setLoading", true);
      commit("setError", "");

      return invoiceService
        .getInvoices()
        .then((value) => {
          commit("getInvoices", value.data);
        })
        .catch((error) => {
          console.error("Failed to load invoices:", error);
          commit(
            "setError",
            getErrorMessage(error, "We couldn't load your payments. Please try again.")
          );
        })
        .finally(() => {
          commit("setLoading", false);
        });
    },

    /*
     * Stripe invoice / receipt links.
     *
     * Reports through state and never rejects, exactly like `getInvoices`
     * above — the payments page treats missing paperwork as a degraded
     * download button, not as a failed page. Rejecting here would force every
     * caller into a `.catch` whose only sensible body is "ignore it", and the
     * one time someone forgot we would be back to unhandled rejections in the
     * console.
     *
     * Note this deliberately does NOT clear `documents` on failure: a refresh
     * that cannot reach Stripe should leave the links the user could already
     * see working, rather than disabling buttons that are perfectly good.
     */
    getStripeDocuments({ commit }) {
      commit("setDocumentsLoading", true);
      commit("setDocumentsError", "");

      return getStripeDocuments()
        .then((byStripeId) => {
          commit("setDocuments", byStripeId);
        })
        .catch((error) => {
          // 503 is the backend saying Stripe is not configured on this
          // deployment. That is not a fault the customer can act on, so it gets
          // the same neutral wording as any other failure to reach it.
          console.error("Failed to load Stripe documents:", error);
          commit(
            "setDocumentsError",
            getErrorMessage(
              error,
              "We couldn't reach Stripe for your invoices. Your payment records are unaffected."
            )
          );
          // Settle the pending state even on failure. Without this, `documents`
          // stays `undefined` forever and every row shows a permanent
          // "checking…" button for a check that will never finish.
          commit("settleDocuments");
        })
        .finally(() => {
          commit("setDocumentsLoading", false);
        });
    },
  },
  mutations: {
    getInvoices(state, item) {
      state.invoices = item;
    },
    setError(state, message) {
      state.error = message;
    },
    setLoading(state, value) {
      state.loading = value;
    },
    setDocuments(state, byStripeId) {
      state.documents = byStripeId;
    },
    setDocumentsError(state, message) {
      state.documentsError = message;
    },
    setDocumentsLoading(state, value) {
      state.documentsLoading = value;
    },
    /** Marks the fetch as answered without discarding anything already held. */
    settleDocuments(state) {
      if (state.documents === undefined) state.documents = new Map();
    },
  },
};
